import React from "react";
import { BookOpen, Plus, Settings2 } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle, Callout, EmptyState, StatusBadge,
} from "../../ui";
import Shimmer from "../../Common/Shimmer";
import SyllabusView from "../../Syllabus/SyllabusView";

/**
 * CourseSyllabusTab — the course's active syllabus, read-only. It is edited,
 * imported and versioned in the syllabus builder (`/courses/:id/syllabus`);
 * this tab is where an admin checks what a new batch will be planned from.
 *
 * Props:
 *   syllabus — the `/syllabus/template/:courseId` resource (null data = none)
 *   summary  — courseDetails.syllabusSummary()
 *   onManage — opens the builder; absent for someone who may not edit it
 */
const CourseSyllabusTab = ({ syllabus, summary, onManage }) => {
  if (syllabus.loading && syllabus.data === null && !syllabus.error) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-3" aria-busy="true" aria-label="Loading syllabus">
        <Shimmer height={64} />
        <Shimmer height={48} count={4} />
      </div>
    );
  }

  if (syllabus.error) {
    return (
      <Callout
        tone="danger"
        role="alert"
        action={<AppButton size="xs" variant="outline" onClick={syllabus.reload}>Try again</AppButton>}
      >
        {syllabus.error?.response?.data?.message || "Could not load this course's syllabus."}
      </Callout>
    );
  }

  if (!summary) {
    return (
      <AppCard>
        <EmptyState
          icon={BookOpen}
          title="No syllabus yet"
          description="Plan the course day by day or module by module, or import it from a spreadsheet. Every batch started from this course takes its class plan from here."
          action={onManage && (
            <AppButton onClick={onManage}>
              <Plus size={16} aria-hidden="true" /> Create syllabus
            </AppButton>
          )}
        />
      </AppCard>
    );
  }

  return (
    <AppCard as="section" aria-labelledby="course-syllabus-title">
      <AppCardHeader className="tw-flex-row tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
        <div className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-2">
          <AppCardTitle id="course-syllabus-title">Syllabus</AppCardTitle>
          <StatusBadge status="success">Active · version {summary.version}</StatusBadge>
          <span className="tw-text-[0.8125rem] tw-text-content-subtle">
            {summary.type === "module" ? "Module-wise" : "Day-wise"}
          </span>
        </div>
        {onManage && (
          <AppButton size="sm" variant="outline" onClick={onManage}>
            <Settings2 size={16} aria-hidden="true" /> Open syllabus builder
          </AppButton>
        )}
      </AppCardHeader>
      <AppCardContent>
        <SyllabusView template={syllabus.data} />
      </AppCardContent>
    </AppCard>
  );
};

export default CourseSyllabusTab;
